
import React from "react";
import { useNavigate } from "react-router-dom";
import { ClockArrowDown, CalendarDays, MailCheck, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const FeaturesCheckList: React.FC = () => {
  const navigate = useNavigate();
  
  const timeFeatures = [
    {
      icon: <ClockArrowDown className="h-6 w-6 text-[#FA8C00]" />,
      title: "Average 15+ hours saved weekly",
      description: "Studio owners report getting back 15+ hours every week that used to be spent answering parent questions."
    },
    {
      icon: <CalendarDays className="h-6 w-6 text-[#4ba3c7]" />,
      title: "24/7 Parent self-service portal",
      description: "Parents access schedules, make-up classes, and payment information on their own schedule, not yours."
    },
    {
      icon: <MailCheck className="h-6 w-6 text-[#7ad4a0]" />,
      title: "80% fewer billing questions",
      description: "Parents can view their own invoices, payment history, and make payments without contacting you."
    },
    {
      icon: <CalendarCheck className="h-6 w-6 text-[#F75C57]" />,
      title: "Self-service scheduling",
      description: "Parents can schedule make-up classes and check availability without sending a single text message."
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-[#FDE1D3]/20">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-left mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Take Back Your Evenings & Weekends
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-8">
            Stop answering the same parent questions over and over. Give them the tools to help themselves.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {timeFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
              >
                <div className="flex items-start">
                  <div className="mr-4 p-3 bg-gray-50 rounded-lg">
                    {feature.icon}
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-12 bg-[#FFDEE2]/30 p-8 rounded-xl border border-[#FFDEE2] relative overflow-hidden">
            <div className="absolute -right-6 -top-6 w-32 h-32 bg-[#FFDEE2]/50 rounded-full blur-xl"></div>
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
                <div className="mb-6 md:mb-0 md:mr-8 text-left">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    "I was spending 2-3 hours every night responding to parent texts. Now I rarely get any!"
                  </h3>
                  <p className="text-gray-600 italic">
                    — Mikayla, Dance Studio Owner
                  </p>
                </div>
                <Button 
                  className="text-lg py-6 px-8 bg-[#FA8C00] hover:bg-[#FA8C00]/90 whitespace-nowrap" 
                  onClick={() => navigate("/business-trial")}
                >
                  Start Your Free Trial
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesCheckList;
